'use client'

import { useAppDispatch } from '@/lib/store'
import { getCartItemLineTotal, removeFromCart, resolveCartItemUnitPrice, updateCartItem } from '@/lib/store/features/cart/cartSlice'
import { CartLineItem } from '@/lib/types/operations/cart'
import type { PriceTier } from '@/lib/utils/price-resolution'
import { normalizePriceBreaks, rollCrossoverYards, toNullableNumber } from '@/lib/utils/price-resolution'
import { formatUnit } from '@/lib/utils/unit-conversion'
import { ImagePlaceholder } from '@/components/ui/image-placeholder'
import { useCallback, useMemo, useState } from 'react'

interface CartItemExpandedProps {
  item: CartLineItem
  onCollapse: () => void
  priceTier: PriceTier
}

function getThumbnailUrl(item: CartLineItem): string | null {
  if (item.variant?.media?.url) return item.variant.media.url
  if (item.product.media?.length) return item.product.media[0].url
  return null
}

export function CartItemExpanded({ item, onCollapse, priceTier }: CartItemExpandedProps) {
  const dispatch = useAppDispatch()
  const unit = formatUnit(item.unitOfMeasure ?? 'FOOT')
  const thumbnail = getThumbnailUrl(item)
  const swatchHex = (item.variant as { swatchHex?: string | null } | undefined)?.swatchHex ?? null

  const [lengthPerPiece, setLengthPerPiece] = useState<string>(
    String(item.lengthPerPiece ?? 0)
  )
  const [note, setNote] = useState<string>(item.note ?? '')

  const dispatchUpdate = useCallback(
    (fields: Partial<{
      quantity: number
      pieces: number
      lengthPerPiece: number
      note: string
    }>) => {
      dispatch(
        updateCartItem({
          cartItemId: item.id,
          quantity: fields.quantity ?? item.quantity,
          pieces: fields.pieces ?? item.pieces,
          lengthPerPiece: fields.lengthPerPiece ?? item.lengthPerPiece ?? undefined,
          note: fields.note ?? item.note ?? undefined,
        })
      )
    },
    [dispatch, item]
  )

  /** Mirrors the server's jump-to-roll guard (reserve-order.ts): cuts are only
   *  sold below the length where cut pricing meets the roll price. */
  const crossover = useMemo(() => {
    const variant = item.variant
    if (!variant || item.purchaseMode !== 'custom-cut') return null
    return rollCrossoverYards({
      breaks: normalizePriceBreaks((variant as { priceBreaks?: unknown }).priceBreaks),
      baseRate: toNullableNumber(variant.retailPrice) ?? 0,
      rollPrice: toNullableNumber(variant.rollPrice),
      rollLengthUnits: toNullableNumber(variant.rollLengthUnits)
    })
  }, [item.variant, item.purchaseMode])

  const handleLengthBlur = useCallback(() => {
    const parsed = parseFloat(lengthPerPiece)
    if (isNaN(parsed) || parsed <= 0) return
    // Whole units for yard goods (the configurator's contract), capped below
    // the crossover length: the server rejects crossover-length cuts
    // (CUT_PRICES_AS_ROLL), so the cart must never build one.
    let next = item.unitOfMeasure === 'YARD' ? Math.max(1, Math.round(parsed)) : parsed
    if (crossover !== null && next >= crossover) next = crossover - 1
    if (next <= 0) return
    setLengthPerPiece(String(next))
    dispatchUpdate({ lengthPerPiece: next })
  }, [lengthPerPiece, dispatchUpdate, crossover, item.unitOfMeasure])

  const handleNoteBlur = useCallback(() => {
    dispatchUpdate({ note })
  }, [note, dispatchUpdate])

  const handlePiecesChange = useCallback(
    (delta: number) => {
      const newPieces = Math.max(1, item.pieces + delta)
      dispatchUpdate({ pieces: newPieces })
    },
    [item.pieces, dispatchUpdate]
  )

  const handleQuantityChange = useCallback(
    (delta: number) => {
      const newQty = Math.max(1, item.quantity + delta)
      dispatchUpdate({ quantity: newQty })
    },
    [item.quantity, dispatchUpdate]
  )

  const lineTotal = getCartItemLineTotal(item, priceTier)
  const perUnitPrice = resolveCartItemUnitPrice(item, priceTier)

  return (
    <div className="rounded-lg bg-surface p-4 border border-border">
      {/* Header row */}
      <div className="flex items-start gap-3 mb-3">
        {/* Thumbnail */}
        <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-md bg-overlay">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={item.product.name ?? 'Product'}
              className="h-full w-full object-cover"
            />
          ) : (
            <ImagePlaceholder
              alt={`Image needed: product photo of ${item.product.name ?? 'this product'}`}
              className="h-full w-full"
              rounded="rounded-md"
              iconSize="sm"
            />
          )}
          {!thumbnail && swatchHex && (
            <span
              className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border border-divider"
              style={{ backgroundColor: swatchHex }}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Name + price per unit */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {item.product.name}
          </p>
          {item.variant?.name && (
            <p className="text-xs text-muted">{item.variant.name}</p>
          )}
          <p className="text-xs text-muted mt-0.5 tabular-nums">
            ${perUnitPrice.toFixed(2)}/{unit}
          </p>
        </div>

        {/* Remove button */}
        <button
          type="button"
          onClick={() => dispatch(removeFromCart({ cartItemId: item.id }))}
          className="text-muted hover:text-danger transition-colors text-lg leading-none flex-shrink-0"
          aria-label={`Remove ${item.product.name} from cart`}
        >
          &times;
        </button>
      </div>

      {/* Custom-cut editing */}
      {item.purchaseMode === 'custom-cut' && (
        <div className="space-y-2">
          {/* Length input */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted w-16">Length</label>
            <input
              type="number"
              value={lengthPerPiece}
              onChange={(e) => setLengthPerPiece(e.target.value)}
              onBlur={handleLengthBlur}
              min={1}
              max={crossover !== null ? crossover - 1 : undefined}
              step={item.unitOfMeasure === 'YARD' ? 1 : 'any'}
              className="w-24 rounded bg-background border border-border px-2 py-1 text-sm text-foreground focus:outline-none focus:border-[var(--focus)]"
            />
            <span className="text-xs text-muted">{unit}</span>
          </div>

          {/* Pieces stepper */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted w-16">Pieces</label>
            <div className="flex items-center rounded border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => handlePiecesChange(-1)}
                className="px-2 py-1 text-sm text-foreground bg-background hover:bg-overlay transition-colors"
                aria-label="Decrease pieces"
              >
                -
              </button>
              <span className="px-3 py-1 text-sm text-foreground bg-background min-w-[2.5rem] text-center">
                {item.pieces}
              </span>
              <button
                type="button"
                onClick={() => handlePiecesChange(1)}
                className="px-2 py-1 text-sm text-foreground bg-background hover:bg-overlay transition-colors"
                aria-label="Increase pieces"
              >
                +
              </button>
            </div>
          </div>

          {/* Note */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted w-16">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={handleNoteBlur}
              placeholder="Add a note..."
              className="flex-1 rounded bg-background border border-border px-2 py-1 text-sm text-warning placeholder:text-muted focus:outline-none focus:border-[var(--focus)]"
            />
          </div>

          {/* Calculation line */}
          <p className="text-xs text-muted mt-1">
            {item.lengthPerPiece ?? 0}{unit} x {item.pieces}pcs ={' '}
            {((item.lengthPerPiece ?? 0) * item.pieces).toFixed(1)}{unit} total
          </p>
        </div>
      )}

      {/* Full-roll editing */}
      {item.purchaseMode === 'full-roll' && (
        <div className="space-y-2">
          {/* Quantity stepper */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted w-16">Qty</label>
            <div className="flex items-center rounded border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => handleQuantityChange(-1)}
                className="px-2 py-1 text-sm text-foreground bg-background hover:bg-overlay transition-colors"
                aria-label="Decrease quantity"
              >
                -
              </button>
              <span className="px-3 py-1 text-sm text-foreground bg-background min-w-[2.5rem] text-center">
                {item.quantity}
              </span>
              <button
                type="button"
                onClick={() => handleQuantityChange(1)}
                className="px-2 py-1 text-sm text-foreground bg-background hover:bg-overlay transition-colors"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>

          {/* Note */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted w-16">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={handleNoteBlur}
              placeholder="Add a note..."
              className="flex-1 rounded bg-background border border-border px-2 py-1 text-sm text-warning placeholder:text-muted focus:outline-none focus:border-[var(--focus)]"
            />
          </div>
        </div>
      )}

      {/* Fixed editing */}
      {item.purchaseMode === 'fixed' && (
        <div className="space-y-2">
          {/* Quantity stepper */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted w-16">Qty</label>
            <div className="flex items-center rounded border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => handleQuantityChange(-1)}
                className="px-2 py-1 text-sm text-foreground bg-background hover:bg-overlay transition-colors"
                aria-label="Decrease quantity"
              >
                -
              </button>
              <span className="px-3 py-1 text-sm text-foreground bg-background min-w-[2.5rem] text-center">
                {item.quantity}
              </span>
              <button
                type="button"
                onClick={() => handleQuantityChange(1)}
                className="px-2 py-1 text-sm text-foreground bg-background hover:bg-overlay transition-colors"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line total + collapse */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <button
          type="button"
          onClick={onCollapse}
          className="text-xs text-muted hover:text-foreground transition-colors"
        >
          Collapse
        </button>
        <span className="text-sm font-semibold text-foreground tabular-nums">
          ${lineTotal.toFixed(2)}
        </span>
      </div>
    </div>
  )
}
